import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { BACKEND_URL } from '../../config';
import './Auth.css';

const Login = ({ onRegisterClick }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [connectionTested, setConnectionTested] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  
  const { login, error, networkStatus, checkServerConnection } = useAuth();

  // Test connection on component mount
  useEffect(() => {
    const testConnection = async () => {
      try {
        const isConnected = await checkServerConnection();
        setConnectionResult(isConnected);
      } catch (err) {
        setConnectionResult(false);
      } finally {
        setConnectionTested(true);
      }
    };
    
    testConnection();
  }, [checkServerConnection]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login({ username, password });
    } catch (err) {
      console.error('Login error:', err);
      // If the error is related to fetch, automatically show troubleshooting
      if (err.message.includes('fetch') || err.message.includes('connect')) {
        setShowTroubleshooting(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Retry connection test
  const retryConnection = async () => {
    setConnectionTested(false);
    try {
      const isConnected = await checkServerConnection();
      setConnectionResult(isConnected);
    } catch (err) {
      setConnectionResult(false);
    } finally {
      setConnectionTested(true);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>नाव ट्रैकर लॉगिन</h2>
          <h3>Boat Tracker Login</h3>
        </div>
        
        {connectionTested && !connectionResult && (
          <div className="connection-warning">
            <p>❗ सर्वर से कनेक्शन नहीं हो सका | Server connection failed</p>
            <button onClick={retryConnection} className="retry-btn">
              फिर से प्रयास करें | Retry Connection
            </button>
          </div>
        )}
        
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">यूजरनेम / Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">पासवर्ड / Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          
          {error && (
            <div className="auth-error">
              <p>{error}</p>
              {(error.includes('connect') || error.includes('fetch') || networkStatus !== 'online') && (
                <button 
                  type="button" 
                  className="troubleshoot-btn"
                  onClick={() => setShowTroubleshooting(!showTroubleshooting)}
                >
                  {showTroubleshooting ? 'समस्या निवारण छिपाएं | Hide Troubleshooting' : 'समस्या निवारण दिखाएं | Show Troubleshooting'}
                </button>
              )}
            </div>
          )}
          
          {showTroubleshooting && (
            <div className="troubleshooting-tips">
              <h4>समस्या निवारण सुझाव | Troubleshooting Tips</h4>
              <ul>
                <li>अपने नेटवर्क कनेक्शन की जाँच करें | Check your network connection</li>
                <li>सर्वर पते की जाँच करें: <strong>{BACKEND_URL}</strong></li>
                <li>ब्राउज़र में सुरक्षा चेतावनी को स्वीकार करें | Accept security warnings in your browser</li>
                <li>सेल्फ-साइन्ड सर्टिफिकेट का उपयोग किया गया है | Self-signed certificates are being used</li>
                <li>सर्टिफिकेट चेतावनी पर "उन्नत" या "Advanced" पर क्लिक करें और "प्रोसीड" करें | Click on "Advanced" and then "Proceed" on certificate warnings</li>
                <li>वाई-फाई कनेक्शन बदलें या मोबाइल डेटा का उपयोग करें | Try changing WiFi or using mobile data</li>
              </ul>
              <div className="connection-details">
                <p><strong>नेटवर्क स्थिति | Network Status:</strong> {networkStatus === 'online' ? 'ऑनलाइन | Online' : 'ऑफलाइन | Offline'}</p>
                <p><strong>सर्वर पता | Server Address:</strong> {BACKEND_URL}</p>
                <p><strong>API प्रोटोकॉल | Protocol:</strong> HTTPS (Secure)</p>
              </div>
              <div className="advanced-options">
                <p>अपने ब्राउज़र में सर्टिफिकेट के साथ आगे बढ़ने के लिए:</p>
                <ol>
                  <li>जब सुरक्षा चेतावनी दिखे, "Advanced" या "उन्नत" पर क्लिक करें</li>
                  <li>"Proceed to {new URL(BACKEND_URL).hostname} (unsafe)" या "अनसुरक्षित आगे बढ़ें" पर क्लिक करें</li>
                  <li>लॉगिन शुरू करने से पहले, {BACKEND_URL} पर जाकर सर्टिफिकेट को स्वीकार करें</li>
                </ol>
              </div>
            </div>
          )}
          
          <button 
            type="submit" 
            className={`auth-button ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading ? 'लॉगिन हो रहा है... | Logging in...' : 'लॉगिन | Login'}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>खाता नहीं है? | Don't have an account?</p>
          <button 
            type="button" 
            className="switch-auth-btn"
            onClick={onRegisterClick}
          >
            रजिस्टर करें | Register
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login; 